import { useMutation } from '@tanstack/react-query';
import { getCallTranscript as getCallTranscriptService, CallTranscript } from 'services/api/getCallTranscript';

const MAX_RETRY_TIME = 25000;
const RETRY_INTERVAL = 3000;

// Retry wrapper function
const withRetry = async (
    callId: string,
    maxRetryTime = MAX_RETRY_TIME,
    retryInterval = RETRY_INTERVAL
): Promise<CallTranscript> => {
    const startTime = Date.now();
    const shouldRetry = true;

    while (shouldRetry) {
        try {
            const result = await getCallTranscriptService(callId);

            // Check if speeches array exists and is not empty
            if (result.speeches && result.speeches.length > 0) {
                return result;
            }

            // Check if we've exceeded max retry time
            const elapsedTime = Date.now() - startTime;
            if (elapsedTime >= maxRetryTime) {
                throw new Error('Transcript not ready: speeches field is empty after maximum retry time');
            }

            // Wait before retrying
            await new Promise((resolve) => setTimeout(resolve, retryInterval));
        } catch (error) {
            // Check if we've exceeded max retry time
            const elapsedTime = Date.now() - startTime;
            if (elapsedTime >= maxRetryTime) {
                throw error;
            }

            // Wait before retrying on error
            await new Promise((resolve) => setTimeout(resolve, retryInterval));
        }
    }

    // This should never be reached, but TypeScript needs it
    throw new Error('Unexpected exit from retry loop');
};

export const useGetCallTranscript = () => {
    // Regular mutation without retry
    const {
        mutateAsync: getCallTranscript,
        isPending,
        error,
    } = useMutation({
        mutationFn: getCallTranscriptService,
    });

    // Mutation with retry logic
    const {
        mutateAsync: getCallTranscriptWithRetry,
        isPending: isPendingWithRetry,
        error: errorWithRetry,
    } = useMutation({
        mutationFn: (callId: string) => withRetry(callId),
    });

    return {
        // Without retry
        getCallTranscript,
        isCallTranscriptLoading: isPending,
        error: error,

        // With retry
        getCallTranscriptWithRetry,
        isCallTranscriptWithRetryLoading: isPendingWithRetry,
        errorWithRetry: errorWithRetry,
    };
};
